/**
 * 视频懒加载工具函数 - 支持自动播放
 * 使用 Intersection Observer API 实现视频的按需加载和播放
 * 
 * 使用方法：
 * 1. 在 video 元素上添加 data-lazy-video-autoplay 属性
 * 2. 将 source 的 src 改为 data-src
 * 3. 设置 preload="none"
 * 4. 调用 initLazyVideoAutoplay(selector, options)
 * 
 * 示例：
 * <video data-lazy-video-autoplay preload="none" muted playsinline loop>
 *   <source data-src="video.mp4" type="video/mp4">
 * </video>
 * 
 * initLazyVideoAutoplay('[data-lazy-video-autoplay]', {
 *   threshold: 0.1,
 *   rootMargin: '50px',
 *   autoplay: true
 * });
 */

(function() {
  'use strict';

  /**
   * 初始化视频懒加载（支持自动播放）
   * @param {string|HTMLElement|NodeList} selector - 选择器、元素或元素列表
   * @param {Object} options - 配置选项
   * @param {number} options.threshold - 触发阈值（0-1），默认 0.1
   * @param {string} options.rootMargin - 根边距，默认 '50px 200px'（上下50px，左右200px，水平方向提前更多触发）
   * @param {boolean} options.autoplay - 是否自动播放，默认 true
   * @param {Function} options.onLoad - 视频加载时的回调
   * @param {Function} options.onPlay - 视频播放时的回调
   */
  window.initLazyVideoAutoplay = function(selector, options) {
    options = options || {};
    var threshold = options.threshold !== undefined ? options.threshold : 0.1;
    var rootMargin = options.rootMargin || '50px 200px'; // 上下50px，左右200px，水平方向提前更多触发
    var autoplay = options.autoplay !== undefined ? options.autoplay : true;
    var onLoad = options.onLoad;
    var onPlay = options.onPlay;

    // 检查浏览器是否支持 Intersection Observer
    if (!('IntersectionObserver' in window)) {
      // 回退：直接加载所有视频
      var videos = typeof selector === 'string' 
        ? document.querySelectorAll(selector)
        : selector instanceof NodeList 
        ? selector
        : [selector];
      
      videos.forEach(function(video) {
        if (video && video.tagName === 'VIDEO') {
          loadVideoSource(video, autoplay, onLoad, onPlay);
        }
      });
      return;
    }

    // 获取视频元素
    var videos = typeof selector === 'string' 
      ? document.querySelectorAll(selector)
      : selector instanceof NodeList 
      ? selector
      : [selector];

    if (!videos || videos.length === 0) {
      return;
    }

    // 创建 Intersection Observer
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          var video = entry.target;
          loadVideoSource(video, autoplay, onLoad, onPlay);
          // 加载后停止观察
          observer.unobserve(video);
        }
      });
    }, {
      threshold: threshold,
      rootMargin: rootMargin
    });

    // 观察所有视频
    videos.forEach(function(video) {
      if (video && video.tagName === 'VIDEO') {
        observer.observe(video);
      }
    });
  };

  /**
   * 视频缓存管理
   */
  var videoCache = {
    // 使用 Map 存储已缓存的视频 URL
    cached: new Map(),
    
    // 检查视频是否已缓存
    isCached: function(url) {
      return this.cached.has(url);
    },
    
    // 标记视频为已缓存
    markCached: function(url) {
      this.cached.set(url, true);
    },
    
    // 预加载视频到缓存
    preload: function(url) {
      if (this.isCached(url)) {
        return Promise.resolve();
      }
      
      return new Promise(function(resolve, reject) {
        var preloadVideo = document.createElement('video');
        preloadVideo.muted = true;
        preloadVideo.playsInline = true;
        preloadVideo.preload = 'auto';
        preloadVideo.style.display = 'none';
        preloadVideo.style.position = 'absolute';
        preloadVideo.style.width = '1px';
        preloadVideo.style.height = '1px';
        preloadVideo.style.opacity = '0';
        preloadVideo.style.pointerEvents = 'none';
        
        var onLoadedData = function() {
          preloadVideo.removeEventListener('loadeddata', onLoadedData);
          preloadVideo.removeEventListener('error', onError);
          videoCache.markCached(url);
          if (preloadVideo.parentNode) {
            preloadVideo.parentNode.removeChild(preloadVideo);
          }
          resolve();
        };
        
        var onError = function() {
          preloadVideo.removeEventListener('loadeddata', onLoadedData);
          preloadVideo.removeEventListener('error', onError);
          if (preloadVideo.parentNode) {
            preloadVideo.parentNode.removeChild(preloadVideo);
          }
          reject(new Error('Failed to preload video: ' + url));
        };
        
        preloadVideo.addEventListener('loadeddata', onLoadedData);
        preloadVideo.addEventListener('error', onError);
        
        var source = document.createElement('source');
        source.src = url;
        source.type = 'video/mp4';
        preloadVideo.appendChild(source);
        
        document.body.appendChild(preloadVideo);
        preloadVideo.load();
      });
    }
  };

  /**
   * 加载视频源并播放
   * @param {HTMLVideoElement} video - 视频元素
   * @param {boolean} autoplay - 是否自动播放
   * @param {Function} onLoad - 加载回调
   * @param {Function} onPlay - 播放回调
   */
  function loadVideoSource(video, autoplay, onLoad, onPlay) {
    // 如果已经加载过，跳过
    if (video.dataset.lazyLoaded === 'true') {
      return;
    }

    // 标记为已加载
    video.dataset.lazyLoaded = 'true';

    // 全局视频缓存设置：设置 preload 为 auto 以启用缓存
    // 这样视频会被浏览器缓存，下次访问时加载更快
    if (video.preload === 'none' || !video.hasAttribute('preload')) {
      video.preload = 'auto';
    }
    
    // 获取视频 URL 并检查缓存
    var videoUrl = null;
    var sources = video.querySelectorAll('source[data-src]');
    if (sources.length > 0) {
      videoUrl = sources[0].getAttribute('data-src');
    } else {
      var existingSources = video.querySelectorAll('source[src]');
      if (existingSources.length > 0) {
        videoUrl = existingSources[0].src;
      }
    }
    
    // 如果视频已缓存，可以更快加载
    if (videoUrl && videoCache.isCached(videoUrl)) {
      // 视频已在缓存中，可以更快处理
    }

    // 加载视频源（从 data-src 移动到 src）
    var sources = video.querySelectorAll('source[data-src]');
    sources.forEach(function(source) {
      var videoUrl = source.getAttribute('data-src');
      source.src = videoUrl;
      source.removeAttribute('data-src');
      
      // 标记视频为已缓存（如果视频已经加载过）
      if (videoUrl) {
        videoCache.markCached(videoUrl);
      }
    });

    // 触发 load 事件，开始加载视频
    video.load();
    
    // 监听视频加载完成，标记为已缓存
    if (videoUrl) {
      var onVideoLoadedData = function() {
        videoCache.markCached(videoUrl);
        video.removeEventListener('loadeddata', onVideoLoadedData);
      };
      video.addEventListener('loadeddata', onVideoLoadedData);
    }

    // 调用加载回调
    if (onLoad && typeof onLoad === 'function') {
      onLoad(video);
    }

    function revealVideo(videoEl) {
      if (!videoEl || !videoEl.classList) return;
      videoEl.classList.add('is-ready');

      var posterEl = null;
      if (videoEl.parentElement) {
        posterEl = videoEl.parentElement.querySelector('[data-bg-poster]');
      }

      if (posterEl) {
        posterEl.classList.add('is-hidden');
      }
    }

    var handleVideoReady = function() {
      revealVideo(video);
      video.removeEventListener('canplay', handleVideoReady);
      video.removeEventListener('playing', handleVideoReady);
      video.removeEventListener('loadeddata', handleVideoReady);
    };

    video.addEventListener('canplay', handleVideoReady);
    video.addEventListener('playing', handleVideoReady);
    video.addEventListener('loadeddata', handleVideoReady);

    if (video.readyState >= 3) {
      handleVideoReady();
    }

    // 如果需要自动播放
    if (autoplay) {
      // 设置 autoplay 属性
      video.setAttribute('autoplay', '');
      
      // 等待视频可以播放后再尝试播放
      var tryPlay = function() {
        if (video.readyState >= 3) { // HAVE_FUTURE_DATA 或更高
          var playPromise = video.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise
              .then(function() {
                revealVideo(video);
                // 播放成功
                if (onPlay && typeof onPlay === 'function') {
                  onPlay(video);
                }
              })
              .catch(function(error) {
                console.warn('视频自动播放失败:', error);
                revealVideo(video);
                // 即使播放失败，也触发 onPlay 回调（用于隐藏 poster）
                if (onPlay && typeof onPlay === 'function') {
                  onPlay(video);
                }
              });
          } else if (onPlay && typeof onPlay === 'function') {
            onPlay(video);
          }
        } else {
          // 视频还没准备好，等待 canplay 事件
          video.addEventListener('canplay', function onCanPlay() {
            video.removeEventListener('canplay', onCanPlay);
            tryPlay();
          }, { once: true });
        }
      };
      
      // 如果视频已经可以播放，立即尝试播放
      if (video.readyState >= 3) {
        tryPlay();
      } else {
        // 等待视频可以播放
        video.addEventListener('canplay', function onCanPlay() {
          video.removeEventListener('canplay', onCanPlay);
          tryPlay();
        }, { once: true });
      }
    }
  }

  // 自动初始化：查找所有带有 data-lazy-video-autoplay 属性的视频
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      var autoVideos = document.querySelectorAll('video[data-lazy-video-autoplay]');
      if (autoVideos.length > 0) {
        window.initLazyVideoAutoplay(autoVideos);
      }
    });
  } else {
    var autoVideos = document.querySelectorAll('video[data-lazy-video-autoplay]');
    if (autoVideos.length > 0) {
      window.initLazyVideoAutoplay(autoVideos);
    }
  }

})();

